'use client';

/**
 * <Markdown> — the ONE component that renders model-generated markdown in the
 * app (owner, 2026-09-23: "existing markdown rendering is SUPER basic and looks
 * terrible ... i think we have a renderer already in other places").
 *
 * Pipeline: lib/markdown/renderSafeMarkdownHtml.ts (hardened, private `marked`
 * instance, DOMPurify defense in depth). Styles: styles/markdown.css, which
 * targets the `app-markdown` class applied here.
 *
 * - `variant="document"` (default): reading layout — analysis, overviews, briefs.
 * - `variant="compact"`: tighter spacing for chat bubbles and small cards.
 * Text colour and font size are INHERITED from the container (a chat bubble
 * sets its own colour); a caller's Tailwind classes override the stylesheet,
 * because the stylesheet lives in `@layer components`.
 *
 * Content is rendered whole. A caller that wants a preview owns its own
 * "Show more" — this component never slices or clamps.
 *
 * `onCitationClick` receives the key of a `#citation:<key>` chip (built with
 * `markdownCitationLink`) via ONE delegated click handler. Transcript citations
 * (`#transcript:<bubble>`) keep their `data-transcript-bubble` anchors; their
 * clicks are delegated by the chat/caption containers as before.
 */
import { useCallback, useMemo, type HTMLAttributes, type MouseEvent } from 'react';

import { renderSafeMarkdownHtml } from '@/lib/markdown/renderSafeMarkdownHtml';
import { cn } from '@/lib/utils';

export type MarkdownVariant = 'document' | 'compact';

export interface MarkdownProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'dangerouslySetInnerHTML' | 'content'> {
  /** Untrusted markdown (model output). Rendered whole. */
  content: string | null | undefined;
  variant?: MarkdownVariant;
  /** Composite controls (search options/tiles) must not contain nested links. */
  interactiveLinks?: boolean;
  /** Drop the renderer's trailing newline, so a clamped card's text content is exactly the rendered text. */
  trimEnd?: boolean;
  /** Called with the chip's key when a `#citation:<key>` anchor is clicked. */
  onCitationClick?: (key: string, event: MouseEvent<HTMLDivElement>) => void;
}

export function Markdown({
  content,
  variant = 'document',
  interactiveLinks = true,
  trimEnd = false,
  className,
  onCitationClick,
  onClick,
  ...rest
}: MarkdownProps) {
  const html = useMemo(() => {
    const safe = renderSafeMarkdownHtml(content ?? '');
    const rendered = trimEnd ? safe.trimEnd() : safe;
    // Only remove anchor tags emitted by the safe renderer; their formatted
    // labels remain. Source HTML is already escaped and cannot match here.
    return interactiveLinks ? rendered : rendered.replace(/<\/?a\b[^>]*>/gi, '');
  }, [content, interactiveLinks, trimEnd]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      onClick?.(event);
      if (!onCitationClick || event.defaultPrevented) return;
      const target = event.target as Element | null;
      const chip = target?.closest?.('a[data-citation]');
      if (!chip || !event.currentTarget.contains(chip)) return;
      event.preventDefault();
      onCitationClick(chip.getAttribute('data-citation') ?? '', event);
    },
    [onClick, onCitationClick],
  );

  return (
    <div
      {...rest}
      className={cn('app-markdown', variant === 'compact' && 'app-markdown--compact', className)}
      onClick={onCitationClick || onClick ? handleClick : undefined}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default Markdown;
